import { useEffect, useMemo, useState } from "react";
import { LineConfig } from "../../../components/Charts";
import chartDataFetcher, { type P2RawDataPoint } from "../../../services/api/ChartDataFetch";

export type P2Sistema = "EU" | "Catalunya";

export interface P2FilterValues {
  yearRange?: [number, number];
  sistema: P2Sistema;
  ambit: string;
  chart1Ambits: string[];
}

export interface P2ChartPoint {
  any: string;
  [key: string]: string | number;
}

const FALLBACK_COLORS = [
  "#4F81BD", "#C0504D", "#9BBB59", "#8064A2",
  "#4BACC6", "#F79646", "#2C4770", "#BE4B48",
];

const SISTEMA_COLORS: Partial<Record<P2Sistema, string>> = {
  Catalunya: "#3498DB",
  EU:        "#2ECC71",
};

const SISTEMA_LABELS: Partial<Record<P2Sistema, string>> = {
  Catalunya: "Catalunya",
  EU:        "Europa",
};

function pivotByAmbit(filtered: P2RawDataPoint[]): P2ChartPoint[] {
  const anys = Array.from(new Set(filtered.map((d) => d.any))).sort();

  return anys.map((any) => {
    const point: P2ChartPoint = { any: String(any) };
    filtered
      .filter((d) => d.any === any)
      .forEach((d) => { point[d.ambit] = d.ratio; });
    return point;
  });
}

function buildAmbitLines(filtered: P2RawDataPoint[]): LineConfig[] {
  const ambits = Array.from(new Set(filtered.map((d) => d.ambit))).sort();

  return ambits.map((ambit, i) => ({
    key:   ambit,
    label: ambit,
    color: FALLBACK_COLORS[i % FALLBACK_COLORS.length],
  }));
}

function pivotBySistema(filtered: P2RawDataPoint[]): P2ChartPoint[] {
  const anys = Array.from(new Set(filtered.map((d) => d.any))).sort();

  return anys.map((any) => {
    const point: P2ChartPoint = { any: String(any) };
    filtered
      .filter((d) => d.any === any)
      .forEach((d) => {
        const label = SISTEMA_LABELS[d.sistema as P2Sistema] ?? d.sistema;
        point[label] = d.ratio;
      });
    return point;
  });
}

function buildSistemaLines(filtered: P2RawDataPoint[]): LineConfig[] {
  const sistemes = Array.from(
    new Set(filtered.map((d) => d.sistema as P2Sistema))
  ).sort() as P2Sistema[];

  return sistemes.map((s, i) => ({
    key:   SISTEMA_LABELS[s] ?? s,
    label: SISTEMA_LABELS[s] ?? s,
    color: SISTEMA_COLORS[s] ?? FALLBACK_COLORS[i % FALLBACK_COLORS.length],
  }));
}

export function useP2Data(filters: P2FilterValues) {
  const [rawData, setRawData] = useState<P2RawDataPoint[]>(
    () => chartDataFetcher.getCachedSync<P2RawDataPoint[]>("p2") ?? []
  );
  const [loading, setLoading] = useState(
    () => chartDataFetcher.getCachedSync<P2RawDataPoint[]>("p2") === null
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (chartDataFetcher.getCachedSync("p2") !== null) return;

    let cancelled = false;

    setLoading(true);
    setError(null);

    chartDataFetcher
      .getP2Data()
      .then((data) => { if (!cancelled) setRawData(data); })
      .catch((e: unknown) => {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "Error carregant dades");
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, []);

  const availableAmbits = useMemo(() => {
    return Array.from(new Set(rawData.map((d) => d.ambit))).sort();
  }, [rawData]);

  const yearFiltered = useMemo(() => {
    if (!filters.yearRange) return rawData;
    const [min, max] = filters.yearRange;
    return rawData.filter((d) => d.any >= min && d.any <= max);
  }, [rawData, filters.yearRange]);

  const { chart1Data, chart1Lines } = useMemo(() => {
    let filtered = yearFiltered.filter((d) => d.sistema === filters.sistema);
    if (filters.chart1Ambits.length > 0) {
      filtered = filtered.filter((d) => filters.chart1Ambits.includes(d.ambit));
    }
    return {
      chart1Data: pivotByAmbit(filtered),
      chart1Lines: buildAmbitLines(filtered),
    };
  }, [yearFiltered, filters.sistema, filters.chart1Ambits]);

  const { chart2Data, chart2Lines } = useMemo(() => {
    const filtered = yearFiltered.filter((d) => d.ambit === filters.ambit);
    return {
      chart2Data: pivotBySistema(filtered),
      chart2Lines: buildSistemaLines(filtered),
    };
  }, [yearFiltered, filters.ambit]);

  return { chart1Data, chart1Lines, chart2Data, chart2Lines, availableAmbits, loading, error };
}
